/**
 * Whiteboard board-result artifacts.
 *
 * A "board result" is the durable record of one whiteboard exercise from a
 * live session: what was on the board, whether the learner solved it, and how
 * many attempts it took. Results feed two consumers:
 *
 * 1. Session memory — results are attached to the session-summary and
 *    tutor-diary generation prompts (both the frontend
 *    /api/progress/* pipeline and the agent-side session_reports artifact
 *    worker) so the next session can open with "last time you nailed word
 *    order but ser/estar is still shaky".
 * 2. The end-of-session board recap gallery, where each solved board becomes
 *    a tappable flashcard.
 *
 * The Python agent produces the same wire shape from
 * lib/whiteboard_runtime.py (WhiteboardState.export_session_results).
 */
import type { WhiteboardSpec } from "./spec";

export type WhiteboardBoardResultStatus = "shown" | "attempted" | "resolved" | "dismissed";

/** Wire/persistence shape shared with the Python agent runtime. */
export interface WhiteboardBoardResult {
  exerciseId: string;
  kind: WhiteboardSpec["kind"];
  /** Human-readable label of what was on the board (key sentence/phrase). */
  label: string;
  status: WhiteboardBoardResultStatus;
  attempts: number;
  correct: number;
  incorrect: number;
}

/** One board from a session: the full spec plus its durable result. */
export interface WhiteboardSessionBoard {
  spec: WhiteboardSpec;
  result: WhiteboardBoardResult;
}

export const MAX_WHITEBOARD_BOARD_RESULTS = 20;

function firstNonEmpty(...values: Array<string | undefined | null>): string {
  for (const value of values) {
    const text = String(value ?? "").trim();
    if (text) return text;
  }
  return "";
}

/**
 * The single sentence/phrase that best represents a board — used as the
 * board-result label and as the flashcard front for the recap gallery.
 */
export function whiteboardKeySentence(spec: WhiteboardSpec): string {
  if (spec.kind === "gap_fill") {
    return spec.sentence.includes("___")
      ? spec.sentence.replace(/_{3,}/, spec.answer)
      : spec.sentence;
  }
  if (spec.kind === "quick_choice") {
    if (spec.answerIndex !== undefined && spec.options[spec.answerIndex]) {
      return spec.options[spec.answerIndex];
    }
    return spec.question;
  }
  if (spec.kind === "word_tiles") {
    return spec.answer;
  }
  if (spec.kind === "reading_story") {
    return firstNonEmpty(spec.title, spec.story);
  }
  if (spec.kind === "equation_balance") {
    return spec.equation;
  }

  for (const block of spec.blocks) {
    switch (block.type) {
      case "pronunciation_focus":
        return block.phrase;
      case "contrast_pair": {
        const pair = block.pairs[0];
        if (pair?.answer === "left" || pair?.answer === "right") return pair[pair.answer];
        if (pair?.answer === "both") return `${pair.left} | ${pair.right}`;
        break;
      }
      case "sentence_repair":
        return firstNonEmpty(block.corrected, block.original);
      case "translation_choice": {
        const natural = block.options.find((option) => option.quality === "natural");
        return firstNonEmpty(block.answer, natural?.text, block.source);
      }
      case "slot_sentence": {
        let filled = block.sentence;
        for (const answer of block.answers) {
          filled = filled.replace(/_{3,}/, answer);
        }
        return filled;
      }
      case "phrase_bank_roleplay": {
        const recommended = block.phrases.find((phrase) => phrase.recommended);
        return firstNonEmpty(recommended?.text, block.phrases[0]?.text, block.scenario);
      }
      case "listening_discrimination":
        return block.answer;
      default:
        break;
    }
  }

  return firstNonEmpty(spec.title, `${spec.blocks[0]?.type ?? "composed"} exercise`);
}

function truncate(text: string, limit: number): string {
  return text.length > limit ? `${text.slice(0, limit - 1)}…` : text;
}

/** Best-effort parse of untrusted payloads (API bodies, agent_metadata). */
export function sanitizeWhiteboardBoardResults(input: unknown): WhiteboardBoardResult[] {
  if (!Array.isArray(input)) return [];
  const results: WhiteboardBoardResult[] = [];
  for (const item of input) {
    if (!item || typeof item !== "object") continue;
    const row = item as Record<string, unknown>;
    const kind = String(row.kind ?? "").trim();
    const label = String(row.label ?? "").trim();
    const status = String(row.status ?? "").trim();
    if (!kind || !label) continue;
    if (!["shown", "attempted", "resolved", "dismissed"].includes(status)) continue;
    results.push({
      exerciseId: truncate(String(row.exerciseId ?? "").trim(), 80),
      kind: kind as WhiteboardSpec["kind"],
      label: truncate(label, 160),
      status: status as WhiteboardBoardResultStatus,
      attempts: Math.max(0, Math.min(99, Number(row.attempts) || 0)),
      correct: Math.max(0, Math.min(99, Number(row.correct) || 0)),
      incorrect: Math.max(0, Math.min(99, Number(row.incorrect) || 0)),
    });
    if (results.length >= MAX_WHITEBOARD_BOARD_RESULTS) break;
  }
  return results;
}

function describeResultLine(result: WhiteboardBoardResult): string {
  const kindLabel = result.kind.replace(/_/g, " ");
  const outcome =
    result.status === "resolved"
      ? result.correct === 0 && result.incorrect === 0
        ? "completed (not correctness-graded)"
        : result.incorrect > 0
          ? `solved after ${result.attempts} attempts (${result.incorrect} wrong first)`
          : "solved"
      : result.status === "dismissed"
        ? result.attempts > 0
          ? `dismissed unsolved after ${result.attempts} attempts`
          : "dismissed without trying"
        : result.status === "attempted"
          ? `attempted ${result.attempts}x, not solved`
          : "shown, no interaction";
  return `- ${kindLabel}: "${result.label}" — ${outcome}`;
}

/**
 * Compact evidence block appended to session-summary / tutor-diary prompts.
 * Returns "" when there is nothing useful to report.
 */
export function formatWhiteboardResultsForPrompt(results: WhiteboardBoardResult[]): string {
  if (!results.length) return "";
  const lines = results.slice(0, MAX_WHITEBOARD_BOARD_RESULTS).map(describeResultLine);
  return [
    "Whiteboard exercise results (interactive boards shown during the session; use these to make strengths and areas-to-improve concrete):",
    ...lines,
  ].join("\n");
}
